import dayjs from "dayjs";
import {
  cloneElement,
  type MouseEvent,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  getCellTrend,
  getColumnPath,
  getLeafIndex,
  getPendingTrend,
  getSectionTitle,
  type ICellPoint,
} from "src/components/pages/Reports/Report/Charts/cellTrend.utils";
import CellTrendPopup, {
  HOVER_DELAY,
  type ITrendAnchor,
  LEAVE_DELAY,
} from "src/components/pages/Reports/Report/Charts/CellTrendPopup";
import { isNumericValue } from "src/components/pages/Reports/Report/Charts/Chart.utils";
import useFieldHistory, {
  FIELD_HISTORY_ENABLED,
  type IFieldHistoryParams,
} from "src/generator/hooks/useFieldHistory";
import { type ICol, type ISection } from "src/interfaces";
import { useSelector } from "src/redux/hooks";

// An entry that is not saved yet has no lbeid.
const CURRENT_LBEID = "current";
// A row's label is not always its first cell, so the row goes unnamed.
const SCOPE = "This cell's value across entries";

interface ILabelRef {
  sectionIndex: number;
  rowIndex: number;
  subRowIndex?: number;
  colIndex: number;
  name?: string;
}

// The generator keeps a row's cells keyed by position, not in an array.
const toCols = (cols: unknown): ICol[] =>
  Array.isArray(cols)
    ? cols
    : cols && typeof cols === "object"
      ? Object.values(cols)
      : [];

const getTitle = (sections: ISection[] | undefined, ref: ILabelRef) => {
  try {
    const section = sections?.[ref.sectionIndex];
    const row = section?.rows?.[ref.rowIndex];
    const cols = toCols(
      ref.subRowIndex === undefined
        ? row?.cols
        : row?.subrows?.[ref.subRowIndex]?.cols,
    );
    const leafIndex = getLeafIndex(cols, ref.colIndex);
    const path = getColumnPath(section?.headers, leafIndex);
    return (
      [getSectionTitle(sections, ref.sectionIndex), ...path]
        .filter(Boolean)
        .join(" · ") || ref.name
    );
  } catch {
    return ref.name;
  }
};

interface IEntryTrendPopup {
  children: ReactElement;
  value: unknown;
  name?: string;
  suffix?: string;
  sectionIndex?: number;
  subSectionIndex?: number;
  rowIndex?: number;
  subRowIndex?: number;
  colIndex?: number;
  disabled?: boolean;
}

const EntryTrendPopup = ({
  children,
  value,
  name,
  suffix,
  sectionIndex,
  subSectionIndex,
  rowIndex,
  subRowIndex,
  colIndex,
  disabled,
}: IEntryTrendPopup) => {
  const { logbookId, lbeidOrPartId } = useParams();
  const storedLbid = useSelector(
    state => state.generatorSlice.entryDataToSave?.lbid,
  );
  const entrydate = useSelector(
    state =>
      state.generatorSlice.entryDataToSave?.entrydate ??
      state.generatorSlice.lbentry?.entrydate,
  );
  const sections = useSelector(state => state.generatorSlice.lbentry?.sections);
  const lbid = logbookId ?? storedLbid;
  const lbeid = lbeidOrPartId ?? "";
  const [hovered, setHovered] = useState(false);
  const [anchor, setAnchor] = useState<ITrendAnchor | null>(null);
  const hover = useRef<{
    overPopup?: boolean;
    show?: ReturnType<typeof setTimeout>;
    hide?: ReturnType<typeof setTimeout>;
  }>({});

  const params = useMemo<IFieldHistoryParams | null>(
    () =>
      lbid
        ? {
            lbid,
            lbeid,
            sectionIndex: sectionIndex ?? 0,
            subSectionIndex,
            rowIndex: rowIndex ?? 0,
            subRowIndex,
            colIndex: colIndex ?? 0,
          }
        : null,
    [
      lbid,
      lbeid,
      sectionIndex,
      subSectionIndex,
      rowIndex,
      subRowIndex,
      colIndex,
    ],
  );
  const { points } = useFieldHistory(
    params,
    FIELD_HISTORY_ENABLED && hovered && !disabled,
  );

  const trend = useMemo(() => {
    if (!points.length) return null;
    const empty = value == null || String(value).trim() === "";
    if (!empty && !isNumericValue(value)) return null;
    const date = dayjs(entrydate);
    const time = date.isValid() ? date.valueOf() : Date.now();
    const current: ICellPoint = {
      time,
      value: Number(value),
      lbeid: lbeid || CURRENT_LBEID,
      rowIndex: rowIndex ?? 0,
      subRowIndex,
    };
    const others = points
      .filter(point => point.lbeid !== current.lbeid)
      .map(point => ({ ...point, rowIndex: current.rowIndex, subRowIndex }));
    if (empty)
      return getPendingTrend(
        others
          .filter(point => point.time <= time)
          .sort((a, b) => a.time - b.time),
        time,
      );
    const series = [...others, current].sort((a, b) => a.time - b.time);
    return getCellTrend(series, current);
  }, [value, points, entrydate, lbeid, rowIndex, subRowIndex]);

  useEffect(() => {
    const state = hover.current;
    return () => {
      clearTimeout(state.show);
      clearTimeout(state.hide);
    };
  }, []);

  useEffect(() => {
    if (!anchor) return;
    // The popup is fixed, so it would stay behind when the page scrolls.
    const hide = () => setAnchor(null);
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [anchor]);

  if (!FIELD_HISTORY_ENABLED || disabled || !params) return children;

  const handleEnter = (event: MouseEvent<HTMLElement>) => {
    children.props.onMouseEnter?.(event);
    const target = event.currentTarget;
    setHovered(true);
    clearTimeout(hover.current.hide);
    clearTimeout(hover.current.show);
    hover.current.show = setTimeout(() => {
      if (!target.isConnected) return;
      const { top, bottom, left } = target.getBoundingClientRect();
      setAnchor({ top, bottom, left });
    }, HOVER_DELAY);
  };

  const handleLeave = (event: MouseEvent<HTMLElement>) => {
    children.props.onMouseLeave?.(event);
    clearTimeout(hover.current.show);
    clearTimeout(hover.current.hide);
    // Moving onto the popup reports the popup before the input is left.
    hover.current.hide = setTimeout(() => {
      if (!hover.current.overPopup) setAnchor(null);
    }, LEAVE_DELAY);
  };

  return (
    <>
      {cloneElement(children, {
        onMouseEnter: handleEnter,
        onMouseLeave: handleLeave,
      })}
      {anchor && trend && (
        <CellTrendPopup
          anchor={anchor}
          trend={trend}
          title={getTitle(sections, {
            sectionIndex: sectionIndex ?? 0,
            rowIndex: rowIndex ?? 0,
            subRowIndex,
            colIndex: colIndex ?? 0,
            name,
          })}
          scope={SCOPE}
          suffix={suffix ? ` ${suffix}` : ""}
          onMouseEnter={() => {
            clearTimeout(hover.current.hide);
            hover.current.overPopup = true;
          }}
          onMouseLeave={() => {
            hover.current.overPopup = false;
            setAnchor(null);
          }}
        />
      )}
    </>
  );
};

export default EntryTrendPopup;
